import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Button, { ButtonTypes } from 'components/common/button';
import {
  extractSchemaProperties,
  findMissingRequired,
  formatProposedActionArguments,
  formatToolDisplayName,
  initialFormValuesFor,
} from 'components/customer/team_assist/team_assist_proposed_action_helpers';

function ProposedActionCard({ execution, isExecuting, isRejected, msg, onExecute, onReject }) {
  const [errorDetailsExpanded, setErrorDetailsExpanded] = useState(false);
  const proposedAction = msg.proposedAction || {};
  const toolName = proposedAction.toolName || '';
  const displayName = formatToolDisplayName(toolName);
  const schemaProperties = extractSchemaProperties(proposedAction.inputSchema);
  const [formValues, setFormValues] = useState(() => initialFormValuesFor(proposedAction.arguments, schemaProperties));
  const isExecuted = Boolean(execution);
  const isLocked = isExecuted || isRejected;
  const missingRequired = findMissingRequired(formValues, schemaProperties);
  const canExecute = missingRequired.length === 0;

  return (
    <ProposedActionContainer data-aid="teamAssistProposedActionCard">
      <ProposedActionHeader>
        <ProposedActionIcon className="fa fa-exclamation-triangle" />
        <ProposedActionTitleWrap>
          <ProposedActionLabel>Action awaiting review</ProposedActionLabel>
          {displayName && <ProposedActionToolName>{displayName}</ProposedActionToolName>}
        </ProposedActionTitleWrap>
      </ProposedActionHeader>
      {schemaProperties.length > 0 ? (
        <ProposedActionForm data-aid="teamAssistProposedActionForm">
          {schemaProperties.map(field => (
            <ProposedActionField
              disabled={isLocked || isExecuting}
              field={field}
              key={field.name}
              onChange={value => setFormValues(prev => ({ ...prev, [field.name]: value }))}
              value={formValues[field.name]}
            />
          ))}
        </ProposedActionForm>
      ) : (
        // Fallback for proposals emitted before inputSchema was added —
        // keep showing the raw argument JSON so the agent at least sees
        // what would execute.
        <FallbackArgsPreview args={proposedAction.arguments} />
      )}
      {!isLocked && (
        <ProposedActionActions>
          <Button
            aria-label="Execute proposed action"
            buttonType={ButtonTypes.PRIMARY}
            data-aid="teamAssistProposedActionExecuteButton"
            disabled={isExecuting || !canExecute}
            isSmall
            onClick={() => onExecute(formValues)}
          >
            {isExecuting ? 'Executing…' : 'Execute'}
          </Button>
          <Button
            aria-label="Reject proposed action"
            buttonType={ButtonTypes.TEXT}
            data-aid="teamAssistProposedActionRejectButton"
            disabled={isExecuting}
            isSmall
            onClick={onReject}
          >
            Reject
          </Button>
          {!canExecute && (
            <ProposedActionRequiredNote data-aid="teamAssistProposedActionRequiredNote">
              Fill required fields to execute
            </ProposedActionRequiredNote>
          )}
        </ProposedActionActions>
      )}
      {isRejected && !isExecuted && (
        <ProposedActionMutedState data-aid="teamAssistProposedActionRejected">Rejected</ProposedActionMutedState>
      )}
      {isExecuted && !execution.isError && (
        <ProposedActionMutedState data-aid="teamAssistProposedActionExecuted">Executed</ProposedActionMutedState>
      )}
      {isExecuted && execution.isError && (
        <ProposedActionErrorBlock data-aid="teamAssistProposedActionError">
          <ProposedActionErrorHeader>
            <ProposedActionErrorIcon className="fa fa-exclamation-circle" />
            <ProposedActionErrorText>Action failed</ProposedActionErrorText>
            {execution.result && (
              <ProposedActionErrorExpander
                aria-expanded={errorDetailsExpanded}
                data-aid="teamAssistProposedActionErrorToggle"
                onClick={() => setErrorDetailsExpanded(v => !v)}
                type="button"
              >
                {errorDetailsExpanded ? 'Hide error details' : 'Show error details'}
              </ProposedActionErrorExpander>
            )}
          </ProposedActionErrorHeader>
          {errorDetailsExpanded && execution.result && (
            <ProposedActionErrorBody data-aid="teamAssistProposedActionErrorBody">
              {execution.result}
            </ProposedActionErrorBody>
          )}
        </ProposedActionErrorBlock>
      )}
    </ProposedActionContainer>
  );
}

ProposedActionCard.propTypes = {
  execution: PropTypes.shape({
    isError: PropTypes.bool,
    result: PropTypes.string,
  }),
  isExecuting: PropTypes.bool,
  isRejected: PropTypes.bool,
  msg: PropTypes.shape({
    customerId: PropTypes.string,
    id: PropTypes.string,
    proposedAction: PropTypes.shape({
      toolName: PropTypes.string,
      summary: PropTypes.string,
      arguments: PropTypes.any,
      inputSchema: PropTypes.any,
    }),
  }),
  onExecute: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
};

export default ProposedActionCard;

function ProposedActionField({ disabled, field, onChange, value }) {
  const labelId = `team-assist-pa-${field.name}`;
  const label = (
    <ProposedActionFieldLabel htmlFor={labelId}>
      {field.label}
      {field.required && <ProposedActionFieldRequired aria-hidden="true"> *</ProposedActionFieldRequired>}
    </ProposedActionFieldLabel>
  );
  const description = field.description ? (
    <ProposedActionFieldDescription>{field.description}</ProposedActionFieldDescription>
  ) : null;

  if (field.type === 'boolean') {
    return (
      <ProposedActionFieldRow data-aid="teamAssistProposedActionField">
        <ProposedActionFieldCheckboxRow>
          <input
            checked={Boolean(value)}
            data-aid={`teamAssistProposedActionField-${field.name}`}
            disabled={disabled}
            id={labelId}
            onChange={e => onChange(e.target.checked)}
            type="checkbox"
          />
          {label}
        </ProposedActionFieldCheckboxRow>
        {description}
      </ProposedActionFieldRow>
    );
  }

  if (field.type === 'enum') {
    return (
      <ProposedActionFieldRow data-aid="teamAssistProposedActionField">
        {label}
        <ProposedActionFieldSelect
          data-aid={`teamAssistProposedActionField-${field.name}`}
          disabled={disabled}
          id={labelId}
          onChange={e => onChange(e.target.value)}
          value={value == null ? '' : String(value)}
        >
          <option value="">Select…</option>
          {field.enumValues.map(option => (
            <option key={String(option)} value={String(option)}>
              {String(option)}
            </option>
          ))}
        </ProposedActionFieldSelect>
        {description}
      </ProposedActionFieldRow>
    );
  }

  if (field.type === 'number') {
    return (
      <ProposedActionFieldRow data-aid="teamAssistProposedActionField">
        {label}
        <ProposedActionFieldInput
          data-aid={`teamAssistProposedActionField-${field.name}`}
          disabled={disabled}
          id={labelId}
          onChange={e => {
            const raw = e.target.value;
            if (raw === '') {
              onChange('');
            } else {
              const num = Number(raw);
              onChange(Number.isNaN(num) ? '' : num);
            }
          }}
          type="number"
          value={value == null ? '' : String(value)}
        />
        {description}
      </ProposedActionFieldRow>
    );
  }

  const inputType = field.type === 'date' ? 'datetime-local' : 'text';
  return (
    <ProposedActionFieldRow data-aid="teamAssistProposedActionField">
      {label}
      <ProposedActionFieldInput
        data-aid={`teamAssistProposedActionField-${field.name}`}
        disabled={disabled}
        id={labelId}
        onChange={e => onChange(e.target.value)}
        type={inputType}
        value={value == null ? '' : String(value)}
      />
      {description}
    </ProposedActionFieldRow>
  );
}

ProposedActionField.propTypes = {
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    description: PropTypes.string,
    enumValues: PropTypes.array,
    label: PropTypes.string,
    name: PropTypes.string.isRequired,
    required: PropTypes.bool,
    type: PropTypes.oneOf(['boolean', 'date', 'enum', 'number', 'text']).isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.any,
};

function FallbackArgsPreview({ args }) {
  const [expanded, setExpanded] = useState(false);
  const argsPreview = formatProposedActionArguments(args);
  if (!argsPreview) {
    return null;
  }
  return (
    <ProposedActionArgs>
      <ProposedActionExpander
        aria-expanded={expanded}
        data-aid="teamAssistProposedActionArgsToggle"
        onClick={() => setExpanded(v => !v)}
        type="button"
      >
        <i aria-hidden="true" className={`fa fa-caret-${expanded ? 'down' : 'right'}`} />{' '}
        {expanded ? 'Hide arguments' : 'Show arguments'}
      </ProposedActionExpander>
      {expanded && <ProposedActionArgsBlock>{argsPreview}</ProposedActionArgsBlock>}
    </ProposedActionArgs>
  );
}

FallbackArgsPreview.propTypes = {
  args: PropTypes.any,
};

const ProposedActionContainer = styled.div`
  background: ${p => p.theme.colors.yellow100};
  border: 1px solid ${p => p.theme.colors.yellow300};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
  padding: 10px 12px;
`;

const ProposedActionHeader = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 8px;
`;

const ProposedActionIcon = styled.i`
  color: ${p => p.theme.colors.yellow600};
  flex-shrink: 0;
  font-size: 13px;
  margin-top: 3px;
`;

const ProposedActionTitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ProposedActionLabel = styled.span`
  color: ${p => p.theme.colors.yellow700};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`;

const ProposedActionToolName = styled.span`
  color: ${p => p.theme.colors.black};
  font-family: monospace;
  font-size: 13px;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const ProposedActionArgs = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProposedActionExpander = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray700};
  cursor: pointer;
  font-size: 12px;
  padding: 0;

  &:hover {
    color: ${p => p.theme.colors.black};
    text-decoration: underline;
  }
`;

const ProposedActionArgsBlock = styled.pre`
  background: ${p => p.theme.colors.gray100};
  border-radius: 4px;
  color: ${p => p.theme.colors.black};
  font-family: monospace;
  font-size: 12px;
  line-height: 1.4;
  margin: 0;
  max-height: 200px;
  overflow: auto;
  padding: 8px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ProposedActionActions = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  margin-top: 2px;
`;

const ProposedActionMutedState = styled.div`
  color: ${p => p.theme.colors.gray700};
  font-size: 12px;
  font-style: italic;
`;

const ProposedActionForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ProposedActionFieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProposedActionFieldLabel = styled.label`
  color: ${p => p.theme.colors.gray800};
  font-size: 12px;
  font-weight: 600;
`;

const ProposedActionFieldRequired = styled.span`
  color: ${p => p.theme.colors.red400};
`;

const ProposedActionFieldDescription = styled.span`
  color: ${p => p.theme.colors.gray600};
  font-size: 11px;
  line-height: 1.3;
`;

const ProposedActionFieldInput = styled.input`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 4px;
  color: ${p => p.theme.colors.black};
  font-family: inherit;
  font-size: 13px;
  padding: 6px 8px;

  &:focus {
    border-color: ${p => p.theme.colors.green400};
    outline: none;
  }

  &:disabled {
    background: ${p => p.theme.colors.gray100};
    color: ${p => p.theme.colors.gray600};
  }
`;

const ProposedActionFieldSelect = styled.select`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 4px;
  color: ${p => p.theme.colors.black};
  font-family: inherit;
  font-size: 13px;
  padding: 6px 8px;

  &:focus {
    border-color: ${p => p.theme.colors.green400};
    outline: none;
  }

  &:disabled {
    background: ${p => p.theme.colors.gray100};
    color: ${p => p.theme.colors.gray600};
  }
`;

const ProposedActionFieldCheckboxRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const ProposedActionRequiredNote = styled.span`
  color: ${p => p.theme.colors.gray600};
  font-size: 11px;
  font-style: italic;
`;

const ProposedActionErrorBlock = styled.div`
  background: ${p => p.theme.colors.red100};
  border-radius: 4px;
  padding: 8px;
`;

const ProposedActionErrorHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 6px;
`;

const ProposedActionErrorIcon = styled.i`
  color: ${p => p.theme.colors.red400};
  font-size: 12px;
`;

const ProposedActionErrorText = styled.span`
  color: ${p => p.theme.colors.red400};
  flex: 1;
  font-size: 12px;
  font-weight: 600;
`;

const ProposedActionErrorExpander = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.red400};
  cursor: pointer;
  font-size: 11px;
  padding: 0;

  &:hover {
    color: ${p => p.theme.colors.red300};
    text-decoration: underline;
  }
`;

const ProposedActionErrorBody = styled.pre`
  background: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.red300};
  border-radius: 4px;
  color: ${p => p.theme.colors.black};
  font-family: monospace;
  font-size: 12px;
  line-height: 1.4;
  margin: 8px 0 0 0;
  max-height: 240px;
  overflow: auto;
  padding: 8px;
  white-space: pre-wrap;
  word-break: break-word;
`;
